import type { CheckboxProps as RadixCheckboxProps } from '@radix-ui/react-checkbox'
import * as RadixCheckbox from '@radix-ui/react-checkbox'
import * as React from 'react'
import { styled } from 'styled-components'
import { SpaceBetween, tk } from '../../utils'
import { Icon } from '../Icon'
import { Typography } from '../Typography'

export type CheckedChangedEventHandler = (checked: CheckedChangeEvent) => void

export type CheckboxState = boolean | 'indeterminate'

export interface CheckedChangeEvent {
  checked: CheckboxState
  id?: string
  name?: string
  value?: string | number | readonly string[]
}

interface CheckboxProps extends CheckboxBaseProps {
  className?: string
  hasError?: boolean
  label: string
  showLabel?: boolean
  supportText?: string
}

interface CheckboxBaseProps extends Omit<RadixCheckboxProps, 'onCheckedChange'> {
  halfChecked?: boolean
  onCheckedChange?: CheckedChangedEventHandler
}

const SupportText = styled(Typography)`
  color: inherit;
`

const StyledWrapper = styled.label`
  color: ${tk('color_semantic_text')};
  display: block;

  &[aria-invalid='true'] {
    color: ${tk('color_semantic_text_error')};
  }
  &:not([data-disabled='true']) {
    cursor: pointer;

    ${SupportText} {
      color: ${tk('color_semantic_text_subdued')};
    }
  }
`

const StyledText = styled.div`
  display: flex;
  flex-direction: column;
  user-select: none;
`

const CheckboxElement = styled(RadixCheckbox.Root)`
  all: unset;
  background: ${tk('color_semantic_bg_success')};
  border: 2px solid;
  border-color: transparent;
  border-radius: ${tk('radius_medium')};
  flex-shrink: 0;
  height: 18px;
  width: 18px;
  box-sizing: border-box;
  margin: 3px;

  &[aria-invalid='true'] {
    background: ${tk('color_semantic_bg_error')};
    &[data-state='unchecked'] {
      background: ${tk('color_semantic_bg')};
      border-color: ${tk('color_semantic_border_error')};
      &:not(:disabled):hover {
        background: ${tk('color_semantic_bg')};
        border-color: ${tk('color_semantic_border_error_hover')};
      }
    }
    &:not(:disabled):hover {
      background: ${tk('color_semantic_bg_error_hover')};
    }
  }
  &[data-state='unchecked'] {
    background: ${tk('color_semantic_bg')};
    border-color: ${tk('color_semantic_border_strong')};
    &:disabled {
      background: ${tk('color_semantic_bg_disabled')};
      border-color: ${tk('color_semantic_border_strong_disabled')};
    }
    &:not(:disabled):hover {
      background: ${tk('color_semantic_bg')};
      border-color: ${tk('color_semantic_border_strong_hover')};
    }
  }
  &:disabled {
    background: ${tk('color_semantic_bg_success_disabled')};
    border-color: transparent;
    & ~ * {
      color: ${tk('color_semantic_text_disabled')};
    }
  }
  &:focus-visible {
    box-shadow: ${tk('shadow_focus_grey')};
  }
  &:not(:disabled):hover {
    background: ${tk('color_semantic_bg_success_hover')};
  }
`

const StyledIndicator = styled(RadixCheckbox.Indicator)`
  align-items: center;
  color: ${tk('color_semantic_text_on_color')};
  display: flex;
  justify-content: center;
  max-height: 100%;
  max-width: 100%;
`

const SrOnlyLabel = styled(Typography)`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`
/**
 * @deprecated Use `Checkbox` from `@sunwebgroup/ui-components/uds` instead.
 */
export const RadixCheckboxRoot = RadixCheckbox.Root

/**
 * @deprecated Use `Checkbox field` from `@sunwebgroup/ui-components/uds` instead.
 */

export function Checkbox({
  className,
  halfChecked,
  hasError,
  label,
  onCheckedChange,
  showLabel = true,
  supportText,
  ...attributes
}: CheckboxProps) {
  const id = React.useId()

  return (
    <StyledWrapper className={className} aria-invalid={hasError} htmlFor={id} data-disabled={attributes.disabled}>
      <SpaceBetween alignItems="center" direction="x" spacing={tk('spacing_sm')}>
        <CheckboxBase
          halfChecked={halfChecked}
          aria-invalid={hasError}
          onCheckedChange={onCheckedChange}
          id={id}
          {...attributes}
        />
        <StyledText>
          {showLabel ? <Typography $variant="label">{label}</Typography> : <SrOnlyLabel>{label}</SrOnlyLabel>}
          {supportText && <SupportText>{supportText}</SupportText>}
        </StyledText>
      </SpaceBetween>
    </StyledWrapper>
  )
}

/**
 * @deprecated Use `Checkbox field` from `@sunwebgroup/ui-components/uds` instead.
 */
export function CheckboxBase({ id, halfChecked, onCheckedChange, ...attributes }: CheckboxBaseProps) {
  // useState is used to take over control for supporting halfChecked state
  const [checked, setChecked] = React.useState<CheckboxState>('indeterminate')

  const handleCheckedChange = (state: CheckboxState) => {
    if (halfChecked) {
      setChecked(state)
    }
    onCheckedChange?.({
      checked: state,
      name: attributes.name,
      value: attributes.value,
    })
  }

  React.useEffect(() => {
    setChecked(attributes.checked ?? 'indeterminate')
  }, [attributes.checked, halfChecked])

  return (
    <CheckboxElement
      id={id}
      checked={halfChecked ? checked : undefined}
      onCheckedChange={handleCheckedChange}
      {...attributes}
    >
      <StyledIndicator>
        {halfChecked && checked === 'indeterminate' && <Icon glyph="IconMinusSmall" size="md" />}
        {(!halfChecked || checked === true) && <Icon glyph="IconCheckmarkSmall" size="md" />}
      </StyledIndicator>
    </CheckboxElement>
  )
}
